/**
 * The setup page's two numbered cards (docs/setup-redesign/README.md, G1):
 *   1 Game setup — Table · Players · Fog & pace
 *   2 Rules      — Board · Turns · Rewards
 * Desktop and tablet lay each card out as three columns of stacked settings;
 * phone stacks the groups, with non-segmented settings as rows.
 */
import React from 'react'
import { View, Text } from 'react-native'
import { CARD_BONUS_SCHEDULES, CARD_BONUS_SCHEDULE_INFO, fogOfWarOf, gameConfigWithDefaults, withFogPlacement } from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import type { CardBonusSchedule, GameConfig, MapDef, TeamSize, TeamsConfig } from '@warcarp/api-types'
import { describeFixedSetup } from '../../../lib/fixedSetup'
import { snapTeams, teamPlayerOptions, teamSizeOptions, teamsResultLine, TEAM_AUTO_START_HINT, TEAM_AUTO_START_NOTE } from '../../../lib/teamSetup'
import { describeSetupValue, type GameSetupDraft, type SetupFieldKey } from '../../../lib/gameSetupDraft'
import { Input } from '../../ui/Input'
import { BootTimerSelector } from '../BootTimerSelector'
import { CompactOptionSelector } from '../CompactOptionSelector'
import { FogOfWarSelector } from '../FogOfWarSelector'
import { VisibilitySelector } from '../VisibilitySelector'
import { GroupHeading, SetupCard } from './SetupCards'
import { LockedValue, SETUP_AMBER, SetupField, SetupStepper, SetupSwitch, STACKED_CONTROL_HEIGHT, type SetupLayout } from './SetupControls'
import { SetupSegmented } from './SetupSegmented'

export interface SetupSectionProps {
  draft: GameSetupDraft
  onChange: (next: GameSetupDraft) => void
  /** Settings changed since the last save (edit), each marked with a dot. */
  changed: ReadonlySet<SetupFieldKey>
  phone: boolean
  map: MapDef | null | undefined
  /** Card padding for the current tier. */
  padding: number
  /** A stepper's field became empty or out of range (or valid again). */
  onInvalid: (field: string, bad: boolean) => void
}

export const MODE_HELP = [
  'Individual: everyone for themselves.',
  'Teams: play in teams of 2, 3 or 4, share vision in fog of war, and win together.',
]
export const TEAM_SIZE_HELP = 'Team size: how many players are on each team. Every team has the same number of players.'
export const TEAM_PLAYERS_HELP = "Players: only counts that split into equal teams are offered. Greyed-out counts don't fit this map."
export const TEAM_ASSIGNMENT_HELP = [
  'Random: the teams are drawn when the game starts.',
  'Pick seats: players choose their team in the lobby.',
]
export const TERRITORY_HELP = [
  'Random: territories are dealt out evenly and troops are placed for you.',
  'Manual: players take turns claiming one territory at a time. Works with no fog or light fog.',
  'Heavy fog only supports random placement: claiming would show who owns every territory.',
]
export const HEAVY_FOG_NOTE = 'Random: required by heavy fog.'

const LIMIT_VALUES = [1, 2, 3, 4, 5, 6, 8, 10, 15, 20, 30, 50]
/** Unlimited plus common caps; a custom saved value stays selectable. */
function limitOptions(current: number | null) {
  const values = current !== null && !LIMIT_VALUES.includes(current) ? [...LIMIT_VALUES, current].sort((a, b) => a - b) : LIMIT_VALUES
  return [{ value: null, label: 'Unlimited' }, ...values.map((v) => ({ value: v as number | null, label: String(v) }))]
}
const CARD_OPTIONS: ReadonlyArray<{ value: CardBonusSchedule; label: string; detail: string }> =
  CARD_BONUS_SCHEDULES.map((value) => ({ value, label: CARD_BONUS_SCHEDULE_INFO[value].label, detail: CARD_BONUS_SCHEDULE_INFO[value].detail }))
const CARD_HELP = [
  'When a player turns in a set of three cards, they get bonus troops. The schedule decides how many, counting every set turned in by anyone.',
  'Cycle (default): 4, 6, 8, 10, then back to 4. Big sets come and go.',
  'Incremental: 4, 5, 6, 7… one more than the last set.',
  'Fixed: always 5. Cards never snowball.',
  'Climb: 3, 6, 9, 12… three more each time. Starts small, builds steadily.',
  'Surge: 5, 10, 15, 20, 25… five more each time. Late sets swing the game.',
]

const CREATE_TEAMS = { floor: 0, seated: 0, notice: null }

/** Phone: groups one after another. Desktop/tablet: three equal columns. */
function Groups({ phone, groups }: { phone: boolean; groups: Array<{ title: string; body: React.ReactNode }> }) {
  if (phone) {
    return <>{groups.map((g, i) => <View key={g.title}><GroupHeading title={g.title} first={i === 0} />{g.body}</View>)}</>
  }
  return <View testID="setup-columns" className="flex-row" style={{ gap: 22 }}>
    {groups.map((g) => <View key={g.title} style={{ flex: 1, minWidth: 0 }}><GroupHeading title={g.title} first />{g.body}</View>)}
  </View>
}

export function GameSetupCard({ draft, onChange, changed, phone, padding, onInvalid, titlePlaceholder, titleMaxLength, seats, map, mapName = 'This map', teams = CREATE_TEAMS }: SetupSectionProps & {
  titlePlaceholder: string
  titleMaxLength: number
  /** The Players stepper's bounds and its hint ("2–9", "3 seated · min 3"). */
  seats: { min: number; max: number; hint: string }
  mapName?: string
  teams?: {
    /** Lobby: the highest taken seat and the seated count (0 when creating). */
    floor: number
    seated: number
    /** The one-line notice after a map change snapped the format. */
    notice: string | null
  }
}) {
  const row: SetupLayout = phone ? 'row' : 'stacked'
  const set = (patch: Partial<GameSetupDraft>) => onChange({ ...draft, ...patch })
  const setPlayers = React.useCallback((bad: boolean) => onInvalid('maxPlayers', bad), [onInvalid])
  const fog = fogOfWarOf(draft.config)
  const teamConfig = teamsConfigOf(draft.config)
  // Team games always start full: auto-start is on and locked.
  const autoStart = teamConfig
    ? <SetupField label="Auto-start" hint={phone ? TEAM_AUTO_START_HINT : undefined} note={phone ? null : TEAM_AUTO_START_NOTE}
      layout={row} changed={changed.has('autoStart')} separated={phone}>
      <SetupSwitch name="Auto-start" value disabled onChange={() => {}} />
    </SetupField>
    : <SetupField label="Auto-start" layout={row} changed={changed.has('autoStart')} separated={phone}>
      <SetupSwitch name="Auto-start" value={draft.autoStart} onChange={(autoStart) => set({ autoStart })} />
    </SetupField>
  const players = teamConfig
    ? <TeamInputs draft={draft} teams={teamConfig} map={map} mapName={mapName} floor={teams.floor} seated={teams.seated}
      notice={teams.notice} changed={changed} onChange={onChange} />
    : seats.min === seats.max
    ? <SetupField label="Players" layout="stacked" changed={changed.has('maxPlayers')}><LockedValue value={`Exactly ${seats.max}`} /></SetupField>
    : <SetupField label="Players" hint={seats.hint} layout={row} changed={changed.has('maxPlayers')}>
      <SetupStepper name="max players" layout={row} value={draft.maxPlayers} min={seats.min} max={seats.max}
        onChange={(maxPlayers) => set({ maxPlayers })} onInvalid={setPlayers} />
    </SetupField>
  return <SetupCard number={1} title="Game setup" padding={padding} testID="setup-card-game">
    <Groups phone={phone} groups={[
      { title: 'Table', body: <>
        <SetupField label="Title" layout="stacked" changed={changed.has('name')}>
          <View style={{ alignSelf: 'stretch' }}>
            <Input value={draft.name} onChangeText={(name) => set({ name })} placeholder={titlePlaceholder} maxLength={titleMaxLength}
              accessibilityLabel="Title" autoCapitalize="sentences" returnKeyType="done"
              style={{ minHeight: STACKED_CONTROL_HEIGHT, height: STACKED_CONTROL_HEIGHT, paddingVertical: 0, fontSize: 15 }} />
          </View>
        </SetupField>
        <SetupField label="Visibility" layout="stacked" changed={changed.has('isPublic')}>
          <VisibilitySelector isPublic={draft.isPublic} onChange={(isPublic) => set({ isPublic })} />
        </SetupField>
        {!phone && autoStart}
      </> },
      { title: 'Players', body: <>
        <ModeField draft={draft} map={map} mapName={mapName} floor={teams.floor}
          changed={changed.has('teams')} onChange={onChange} />
        {players}
        {phone && autoStart}
      </> },
      { title: 'Fog & pace', body: <>
        <FogOfWarSelector layout={row} value={fog} changed={changed.has('fogOfWar')}
          onChange={(fogOfWar) => set({ config: withFogPlacement({ ...draft.config, fogOfWar }) })} />
        <BootTimerSelector layout={row} value={draft.bootAfterSeconds} changed={changed.has('bootAfterSeconds')} separated={phone}
          onChange={(bootAfterSeconds) => set({ bootAfterSeconds })} />
      </> },
    ]} />
  </SetupCard>
}

/** Mode: Individual | Teams. Switching to Teams starts at teams of 2 and the closest valid count. */
function ModeField({ draft, map, mapName, floor, changed, onChange }: {
  draft: GameSetupDraft
  map: MapDef | null | undefined
  mapName: string
  floor: number
  changed: boolean
  onChange: (next: GameSetupDraft) => void
}) {
  const current = teamsConfigOf(draft.config)
  const fitting = snapTeams(map, { size: 2, assignment: 'random' }, draft.maxPlayers, floor)
  const unavailable = current || fitting ? null : `${mapName} can't seat equal teams.`
  const choose = (mode: 'individual' | 'teams') => {
    if (mode === 'individual') {
      const { teams: _teams, ...config } = draft.config
      onChange({ ...draft, config })
    } else if (fitting) {
      onChange({ ...draft, maxPlayers: fitting.players, autoStart: true, config: { ...draft.config, teams: fitting.teams } })
    }
  }
  return <SetupField label="Mode" layout="stacked" changed={changed} note={unavailable} noteTone="warn"
    help={{ label: 'What are the game modes?', title: 'Mode', message: MODE_HELP, testID: 'mode-help' }}>
    <SetupSegmented value={current ? 'teams' : 'individual'} onChange={choose} options={[
      { value: 'individual', label: 'Individual', testID: 'setup-mode-individual' },
      { value: 'teams', label: 'Teams', disabled: !!unavailable, testID: 'setup-mode-teams' },
    ]} />
  </SetupField>
}

/**
 * Mode = Teams: Team size, Players (only counts that split into equal teams),
 * Assignment and the result line (docs/setup-redesign/teams-config.md).
 * Nothing invalid is selectable: a size change snaps the count, and sizes and
 * counts the map can't seat are disabled with the reason in the "?".
 */
function TeamInputs({ draft, teams, map, mapName, floor, seated, notice, changed, onChange }: {
  draft: GameSetupDraft
  teams: TeamsConfig
  map: MapDef | null | undefined
  mapName: string
  floor: number
  seated: number
  notice: string | null
  changed: ReadonlySet<SetupFieldKey>
  onChange: (next: GameSetupDraft) => void
}) {
  const sizes = teamSizeOptions(map, mapName)
  const counts = teamPlayerOptions(map, teams.size, floor, mapName)
  const setTeams = (next: TeamsConfig, maxPlayers = draft.maxPlayers) =>
    onChange({ ...draft, maxPlayers, autoStart: true, config: { ...draft.config, teams: next } })
  const setSize = (size: TeamSize) => {
    const snapped = snapTeams(map, { ...teams, size }, draft.maxPlayers, floor)
    if (snapped && snapped.teams.size === size) setTeams(snapped.teams, snapped.players)
  }
  const sizeReasons = sizes.filter((o) => o.reason).map((o) => o.reason!)
  const belowFloor = counts.filter((o) => o.players < floor && o.reason?.endsWith('taken.'))
  const floorNote = seated > 0 && belowFloor.length > 0
    ? `${seated} ${seated === 1 ? 'player is' : 'players are'} seated, so ${belowFloor.map((o) => o.players).join(' or ')} ${belowFloor.length === 1 ? 'is' : 'are'} not available.`
    : null
  return <>
    <SetupField label="Team size" layout="stacked" changed={changed.has('teams')}
      help={{ label: 'What is team size?', title: 'Team size', message: [TEAM_SIZE_HELP, ...sizeReasons.slice(0, 1)], testID: 'team-size-help' }}>
      <SetupSegmented value={String(teams.size)} onChange={(v) => setSize(Number(v) as TeamSize)}
        options={sizes.map((o) => ({ value: String(o.size), label: String(o.size), disabled: o.disabled,
          accessibilityLabel: o.disabled ? `Teams of ${o.size}: ${o.reason}` : `Teams of ${o.size}`, testID: `team-size-${o.size}` }))} />
    </SetupField>
    <SetupField label="Players" layout="stacked" changed={changed.has('maxPlayers')}
      help={{ label: 'Which player counts can teams use?', title: 'Players', message: TEAM_PLAYERS_HELP, testID: 'team-players-help' }}>
      <SetupSegmented lines={2} value={String(draft.maxPlayers)} onChange={(v) => setTeams(teams, Number(v))}
        options={counts.map((o) => ({ value: String(o.players), label: o.label, primary: o.primary, detail: o.detail, disabled: o.disabled,
          accessibilityLabel: o.disabled ? `${o.players} players, ${o.teams} teams: ${o.reason}` : `${o.players} players, ${o.teams} teams`,
          testID: `team-players-${o.players}` }))} />
    </SetupField>
    <SetupField label="Assignment" layout="stacked" changed={changed.has('teams')}
      help={{ label: 'How are teams assigned?', title: 'Assignment', message: TEAM_ASSIGNMENT_HELP, testID: 'team-assignment-help' }}>
      <SetupSegmented value={teams.assignment} onChange={(assignment) => setTeams({ ...teams, assignment })} options={[
        { value: 'random', label: 'Random', accessibilityLabel: 'Random teams', testID: 'team-assignment-random' },
        { value: 'manual', label: 'Pick seats', testID: 'team-assignment-manual' },
      ]} />
    </SetupField>
    {(notice || floorNote) && <Text testID="team-setup-note" className="font-typed italic text-xs leading-4" style={{ color: SETUP_AMBER, marginTop: -6, marginBottom: 10 }}>
      {[notice, floorNote].filter(Boolean).join(' ')}
    </Text>}
    <View testID="team-result" className="bg-olive-up rounded-full items-center justify-center" style={{ minHeight: 30, paddingHorizontal: 12, paddingVertical: 6, marginBottom: 6 }}>
      <Text className="font-typed text-canvas text-xs font-bold text-center">{teamsResultLine(teams, draft.maxPlayers)}</Text>
    </View>
  </>
}

export function RulesCard({ draft, onChange, changed, phone, padding, map, onInvalid }: SetupSectionProps) {
  const row: SetupLayout = phone ? 'row' : 'stacked'
  // Heavy fog forces random placement (withFogPlacement), so switching to it
  // flips a manual claim to random.
  const setConfig = (patch: Partial<GameConfig>) => onChange({ ...draft, config: withFogPlacement({ ...draft.config, ...patch }) })
  const heavyFog = fogOfWarOf(draft.config) === 'heavy'
  const rules = gameConfigWithDefaults(draft.config)
  const autoAssign = heavyFog || rules.autoAssignTerritories
  const fixed = map ? describeFixedSetup(map) : null
  const pinned = (map?.fixedSetup?.config ?? {}) as Partial<GameConfig>
  const invalid = React.useMemo(() => ({
    startingTroops: (bad: boolean) => onInvalid('startingTroops', bad),
    eliminatePlayerBonus: (bad: boolean) => onInvalid('eliminatePlayerBonus', bad),
  }), [onInvalid])
  /** A rule the map pins: same label as its control, the map's value, locked. */
  const locked = (key: keyof GameConfig, label: string) => <SetupField label={label} layout="stacked">
    <LockedValue value={describeSetupValue(key, pinned[key])?.value ?? String(pinned[key])} />
  </SetupField>

  const board = fixed
    ? <>
      <SetupField label="Territory selection" layout="stacked" note={phone ? fixed.territorySelection : null}>
        <LockedValue value={fixed.homeArea} />
      </SetupField>
      <SetupField label="Starting troops" layout="stacked" note={phone ? 'Set by the map, so every player opens the same way.' : null}>
        <LockedValue value={fixed.startingTroops} />
      </SetupField>
      <Text className="font-typed text-ink-soft text-xs leading-4">
        {phone
          ? `★ From ${map?.name}'s special rules (shown under the map).`
          : `★ Set by ${map?.name}'s special rules (under the map). ${fixed.territorySelection} Starting troops are set by the map, so every player opens the same way.`}
      </Text>
    </>
    : <>
      <SetupField label="Territory selection" layout="stacked" changed={changed.has('autoAssignTerritories')} note={heavyFog ? HEAVY_FOG_NOTE : null}
        help={{ label: 'Which territory selection works with fog of war?', title: 'Territory Selection', message: TERRITORY_HELP, testID: 'territory-selection-help' }}>
        <SetupSegmented value={autoAssign} onChange={(v) => setConfig({ autoAssignTerritories: v })} options={[
          { value: true, label: 'Random' },
          { value: false, label: 'Manual', accessibilityLabel: 'Manual claim', disabled: heavyFog },
        ]} />
      </SetupField>
      <SetupField label="Starting troops" hint="per territory" layout={row} changed={changed.has('startingTroops')}>
        <SetupStepper name="starting troops" layout={row} value={rules.startingTroops} min={1} max={50}
          onChange={(startingTroops) => setConfig({ startingTroops })} onInvalid={invalid.startingTroops} />
      </SetupField>
    </>

  const turns = <>
    {'maxAttacksPerTurn' in pinned ? locked('maxAttacksPerTurn', 'Attacks') : <CompactOptionSelector title="Attacks" layout={row}
      value={rules.maxAttacksPerTurn} options={limitOptions(rules.maxAttacksPerTurn)} changed={changed.has('maxAttacksPerTurn')}
      pillTestID="attacks-selector" optionTestID={(v) => `attacks-${v ?? 'unlimited'}`}
      onChange={(maxAttacksPerTurn) => setConfig({ maxAttacksPerTurn })} />}
    {'maxFortifiesPerTurn' in pinned ? locked('maxFortifiesPerTurn', 'Fortifies') : <CompactOptionSelector title="Fortifies" layout={row} separated={phone}
      value={rules.maxFortifiesPerTurn} options={limitOptions(rules.maxFortifiesPerTurn)} changed={changed.has('maxFortifiesPerTurn')}
      pillTestID="fortifies-selector" optionTestID={(v) => `fortifies-${v ?? 'unlimited'}`}
      onChange={(maxFortifiesPerTurn) => setConfig({ maxFortifiesPerTurn })} />}
  </>

  const rewards = <>
    {'eliminatePlayerBonus' in pinned ? locked('eliminatePlayerBonus', 'Eliminate bonus') : <SetupField label="Eliminate bonus" layout={row} changed={changed.has('eliminatePlayerBonus')}>
      <SetupStepper name="eliminate bonus" layout={row} value={rules.eliminatePlayerBonus} min={0} max={50}
        onChange={(eliminatePlayerBonus) => setConfig({ eliminatePlayerBonus })} onInvalid={invalid.eliminatePlayerBonus} />
    </SetupField>}
    {'cardBonusSchedule' in pinned ? locked('cardBonusSchedule', 'Card sets') : <CompactOptionSelector title="Card sets" layout={row} separated={phone}
      value={rules.cardBonusSchedule} options={CARD_OPTIONS} changed={changed.has('cardBonusSchedule')}
      pillTestID="card-sets-selector" optionTestID={(v) => `card-sets-${v}`}
      helpLabel="What do the card set schedules mean?" helpMessage={CARD_HELP}
      note={CARD_OPTIONS.find((o) => o.value === rules.cardBonusSchedule)?.detail} noteUnderControl
      onChange={(cardBonusSchedule) => setConfig({ cardBonusSchedule })} />}
  </>

  return <SetupCard number={2} title="Rules" padding={padding} testID="setup-card-rules">
    <Groups phone={phone} groups={[{ title: 'Board', body: board }, { title: 'Turns', body: turns }, { title: 'Rewards', body: rewards }]} />
  </SetupCard>
}
